import React from 'react'
import { TextInput } from '../../../components/form/textInput'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { Divider } from '../../../components/simple/divider'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { Assessment } from '../assessmentDashboard'
import { LocalTable } from '../../../components/table'
import { Popups } from '../../../providers/popups'
import { TruncatedText } from '../../../components/simple/truncatedText'
import { downloadFile } from '../../../utils'
import { IconDownload } from '../../../icons/library'
import { useNavigate } from 'react-router-dom'

export const ProcessTab: React.FC<{
  assessment: Assessment
  reloadAssessment: () => void
}> = ({ assessment, reloadAssessment }) => {
  const [search, setSearch] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const navigate = useNavigate()
  return (
    <div className="mb-4 mt-2 flex min-w-[500px] max-w-full flex-col items-start gap-2 self-center">
      <div className="flex justify-between self-stretch">
        <Button
          logo
          showLoader={loading}
          disabled={loading}
          onClick={async () => {
            if (!(await Popups.simpleConfirm('Are you sure you want to start processing this assessment?'))) return
            setLoading(true)
            makeRequest('/frontend_api/processAssessment', {
              assessmentID: assessment.id,
            })
              .then(reloadAssessment)
              .catch(showAxiosErrorAsToast)
              .finally(() => setLoading(false))
          }}
        >
          Start {assessment.processed.length > 0 ? 'Reprocess' : 'Process'}
        </Button>
        {assessment.processed.length > 0 && (
          <Button
            buttonStyle={ButtonStyle.ghostLogoTransparent}
            onClick={async () => {
              if (
                !(await Popups.confirm({
                  title: 'Are you sure you want to clear all processed records?',
                  message: (
                    <>
                      <b>Be Careful: This will break all existing document links that have been sent out.</b> You can
                      reprocess and automatically update existing entries without first clearing them.
                    </>
                  ),
                  yesText: 'Clear',
                  cancel: 'Cancel',
                }))
              )
                return

              setLoading(true)
              makeRequest('/frontend_api/clearProcessAssessment', {
                assessmentID: assessment.id,
              })
                .then(reloadAssessment)
                .catch(showAxiosErrorAsToast)
                .finally(() => setLoading(false))
            }}
          >
            Clear All Processed Records
          </Button>
        )}
      </div>
      {assessment.processingNotes && (
        <div className="relative max-h-[600px] overflow-auto rounded bg-warning p-2">
          <b className="pr-5">Notes from previous processing</b>
          <TruncatedText className="whitespace-pre-wrap">{assessment.processingNotes}</TruncatedText>
          <IconDownload
            className="absolute right-2 top-2 cursor-pointer text-black/50"
            onClick={() => {
              const blob = new Blob([assessment.processingNotes || ''], { type: 'text/plain;charset=utf-8' })
              downloadFile(blob, `processing_notes_${assessment.id}.txt`)
            }}
          />
        </div>
      )}
      <Divider className="my-4" />
      <TextInput value={search} onChange={setSearch} placeholder="Search" />
      <LocalTable
        className="self-stretch"
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
          },
          {
            header: 'Student ID',
            accessorKey: 'syncID',
          },
          {
            header: 'Preview As Recipient',
            accessorKey: 'previewLink',
            cell: ({ row }) => (
              <a href={row.original.previewLink} target="_blank">
                preview
              </a>
            ),
          },
        ]}
        data={assessment.processed}
        search={search}
      />
      {assessment.processed.length > 0 && (
        <Button logo onClick={() => navigate('#sendMessage')}>
          Continue
        </Button>
      )}
    </div>
  )
}
